import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';

const STORAGE_KEY = 'zog_cookie_consent_v1';

/**
 * Returns the current consent record (or null if user hasn't decided yet).
/**
 * GDPR cookie-consent record.
 *
 * Stored in localStorage by design — *not* an authentication token. We must
 * remember the user's consent across sessions and tabs, and the W3C TCF v2
 * pattern explicitly recommends client-side persistence. No PII is stored
 * here, only the boolean consent flags + an ISO timestamp.
 * Shape: { essential: true, analytics: bool, marketing: bool, decidedAt: ISOstring }
 */
export const getConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') console.debug('[cookies] getConsent failed:', err);
    return null;
  }
};

const persist = (consent) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    // Broadcast so analytics scripts can react without a reload
    window.dispatchEvent(new CustomEvent('zog:consent-change', { detail: consent }));
  } catch (err) {
    /* private browsing / quota exceeded — best-effort persistence */
    if (process.env.NODE_ENV !== 'production') console.debug('[cookies] persist failed:', err);
  }
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: true, marketing: false });

  useEffect(() => {
    // Never show the banner on admin routes — it overlays the artwork
    // cards' action buttons and blocks admin work.
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
      return undefined;
    }
    // Defer to avoid hurting initial render / CLS
    const t = setTimeout(() => {
      if (!getConsent()) setVisible(true);
    }, 800);
    return () => clearTimeout(t);
  }, []);

  const acceptAll = () => {
    persist({ essential: true, analytics: true, marketing: true, decidedAt: new Date().toISOString() });
    setVisible(false);
  };

  const rejectNonEssential = () => {
    persist({ essential: true, analytics: false, marketing: false, decidedAt: new Date().toISOString() });
    setVisible(false);
  };

  const saveCustom = () => {
    persist({
      essential: true,
      analytics: !!prefs.analytics,
      marketing: !!prefs.marketing,
      decidedAt: new Date().toISOString(),
    });
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          role="dialog"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-body"
          className="fixed bottom-3 left-3 right-3 sm:right-auto sm:max-w-[340px] z-[80] bg-white text-black shadow-lg border border-black/10"
          data-testid="cookie-banner"
        >
          <div className="px-3.5 py-3">
            <div className="flex items-start gap-2 mb-2">
              <p id="cookie-banner-body" className="flex-1 text-[11px] text-neutral-600 leading-snug">
                <span id="cookie-banner-title" className="text-black">We use cookies</span> to run the site and,
                with your OK, for analytics.{' '}
                <Link to="/cookies" className="underline hover:text-black">Cookie policy</Link>
              </p>
              <button
                onClick={rejectNonEssential}
                aria-label="Close — reject non-essential cookies"
                className="text-neutral-400 hover:text-black transition-colors -m-1 p-1"
                data-testid="cookie-banner-close"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {expanded && (
              <div className="space-y-2 mb-2.5">
                <label className="flex items-start gap-2 text-[11px]">
                  <input
                    type="checkbox"
                    checked
                    disabled
                    className="mt-0.5"
                    data-testid="cookie-pref-essential"
                  />
                  <span>
                    <strong className="block">Essential</strong>
                    <span className="text-neutral-500">Required for login, cart, language. Always on.</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-[11px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={prefs.analytics}
                    onChange={(e) => setPrefs((p) => ({ ...p, analytics: e.target.checked }))}
                    className="mt-0.5"
                    data-testid="cookie-pref-analytics"
                  />
                  <span>
                    <strong className="block">Analytics</strong>
                    <span className="text-neutral-500">Anonymous usage data to improve the site.</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-[11px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={prefs.marketing}
                    onChange={(e) => setPrefs((p) => ({ ...p, marketing: e.target.checked }))}
                    className="mt-0.5"
                    data-testid="cookie-pref-marketing"
                  />
                  <span>
                    <strong className="block">Marketing</strong>
                    <span className="text-neutral-500">Personalise gallery emails and remarketing.</span>
                  </span>
                </label>
              </div>
            )}

            <div className="flex items-center gap-1.5">
              {!expanded ? (
                <>
                  <button
                    onClick={acceptAll}
                    className="px-3 py-1.5 bg-black text-white text-[10px] uppercase tracking-[0.15em] hover:bg-neutral-800 transition-colors"
                    data-testid="cookie-accept-all"
                  >
                    Accept all
                  </button>
                  <button
                    onClick={rejectNonEssential}
                    className="px-3 py-1.5 border border-black text-black text-[10px] uppercase tracking-[0.15em] hover:bg-black hover:text-white transition-colors"
                    data-testid="cookie-reject"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => setExpanded(true)}
                    className="text-[10px] uppercase tracking-[0.15em] underline text-neutral-600 hover:text-black px-1.5 py-1.5"
                    data-testid="cookie-customize"
                  >
                    Customise
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={saveCustom}
                    className="px-3 py-1.5 bg-black text-white text-[10px] uppercase tracking-[0.15em] hover:bg-neutral-800 transition-colors"
                    data-testid="cookie-save"
                  >
                    Save preferences
                  </button>
                  <button
                    onClick={acceptAll}
                    className="px-3 py-1.5 border border-black text-black text-[10px] uppercase tracking-[0.15em] hover:bg-black hover:text-white transition-colors"
                    data-testid="cookie-accept-all-expanded"
                  >
                    Accept all
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
